import React, { Fragment, useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Popover, PopoverBody, PopoverHeader, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, Link } from "react-router-dom";
import Select from "react-select";
import { DateRangePicker } from "rsuite";
import moment from "moment";
import Breadcrumb from "../../../layout/breadcrumb";
import CommonDataTable from "../../../share-components/table/commonDataTable";
import DatePicker from "../../../share-components/ui/DateRangePicker";
import withReducer from "../../../store/withReducer";
import { formatterCurrency } from "../../../utils/common";
import orderReducer from "../store";
import { getFrasersPayments } from "../store/frasersPlusSlice";
import { CREDIT_STATUS_OPTIONS, PAYMENT_STATUS_OPTIONS, creditBadge, paymentBadge } from "./constants";

const { afterToday } = DateRangePicker;

/**
 * Frasers Plus Payments — every payment we have taken, or tried to take, on
 * credit, and every refund we have had to hand back to Frasers.
 */
function FrasersPlusPayments() {
    const dataMenu = [
        {
            type: 0,
            text: "Frasers Plus Payments",
        },
    ];

    const dispatch = useDispatch();
    const history = useHistory();
    const frasersState = useSelector(({ order }) => order.frasersPlus);

    const TodayDate = new Date();
    const thirtyDays = new Date().setDate(TodayDate.getDate() - 30);

    const [dateRange, setDateRange] = useState([thirtyDays, TodayDate]);
    const [startDate, endDate] = dateRange;
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [creditStatus, setCreditStatus] = useState(null);
    const [search, setSearch] = useState("");
    const [timer, setTimer] = useState(null);
    const [quickFilter, setQuickFilter] = useState(null);
    const [tableLoading, setTableLoading] = useState(false);

    const customStyles = {
        control: (base) => ({
            ...base,
            minHeight: 50,
            border: "none",
        }),
    };

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, startDate, endDate, page, pageSize, paymentStatus, creditStatus, search, quickFilter]);

    const getData = () => {
        if (startDate !== null && endDate !== null) {
            setTableLoading(true);
            dispatch(
                getFrasersPayments({
                    page,
                    pageSize,
                    start_date: moment(startDate).format("YYYY-MM-DD"),
                    end_date: moment(endDate).format("YYYY-MM-DD"),
                    status: paymentStatus,
                    credit_status: creditStatus,
                    search,
                    quick: quickFilter,
                })
            ).then(() => setTableLoading(false));
        }
    };

    /* ---------------------------------------------------------------- */
    /* The four things someone opens this screen to find                 */
    /* ---------------------------------------------------------------- */

    const counts = frasersState.counts;

    const tiles = [
        { key: "paid", label: "Paid", value: counts.paid, colour: "success" },
        { key: "awaiting", label: "Awaiting payment", value: counts.awaiting, colour: "warning" },
        { key: "not_paid", label: "Not paid", value: counts.not_paid, colour: "danger" },
        { key: "credit_failed", label: "Credit failed", value: counts.credit_failed, colour: "danger" },
        // Orders where the buyer never opened the pay link. No payment row
        // exists, so these come from the orders table.
        { key: "not_started", label: "Not started", value: counts.not_started, colour: "secondary" },
    ];

    const openPayment = (row) => {
        if (row.notStarted) {
            history.push(`${process.env.PUBLIC_URL}/order/${row.orderId}/view`);
            return;
        }
        history.push(`${process.env.PUBLIC_URL}/order/frasers-plus-payments/${row.id}/view`);
    };

    const ErrorPopover = ({ row }) => {
        const [open, setOpen] = useState(false);

        if (!row.lastError) {
            return <span className="text-muted">-</span>;
        }

        return (
            <div>
                <span
                    id={`fp-error-${row.id}`}
                    className="text-danger"
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => setOpen(!open)}
                >
                    View error
                </span>
                <Popover placement="left" isOpen={open} target={`fp-error-${row.id}`} toggle={() => setOpen(!open)}>
                    <PopoverHeader>{row.orderName}</PopoverHeader>
                    <PopoverBody>{row.lastError}</PopoverBody>
                </Popover>
            </div>
        );
    };

    const tableColumns = [
        {
            name: "Order",
            selector: (row) => row.orderName,
            cell: (row) =>
                row.orderId ? (
                    <Link
                        className="text-primary"
                        style={{ cursor: "pointer" }}
                        target="_blank"
                        to={`${process.env.PUBLIC_URL}/order/${row.orderId}/view`}
                    >
                        {row.orderName}
                    </Link>
                ) : (
                    // The marketplace order row can lag the payment by a few seconds.
                    <span>{row.orderName}</span>
                ),
            sortable: false,
            center: false,
        },
        {
            name: "Payment date",
            selector: (row) => row.orderDate,
            sortable: false,
            center: false,
        },
        {
            name: "Customer",
            selector: (row) => row.customer,
            cell: (row) => (
                <div>
                    {row.customer || <span className="text-muted">-</span>}
                    {row.phone ? <div className="text-muted" style={{ fontSize: 11 }}>{row.phone}</div> : null}
                </div>
            ),
            sortable: false,
            center: false,
        },
        {
            name: "Amount",
            selector: (row) => row.amount,
            cell: (row) => formatterCurrency(row.amount, row.currency),
            sortable: false,
            center: false,
        },
        {
            name: "Payment",
            selector: (row) => row.paymentStatus,
            cell: (row) => <span className={`badge badge-${paymentBadge(row.status)}`}>{row.paymentStatus}</span>,
            sortable: false,
            center: false,
        },
        {
            name: "Credited",
            selector: (row) => row.credited,
            cell: (row) =>
                row.creditsCount > 0 ? (
                    <span>
                        {formatterCurrency(row.credited, row.currency)}{" "}
                        <span className="text-muted">of {formatterCurrency(row.amount, row.currency)}</span>
                        {/* An order can be refunded many times; the count says so without opening it. */}
                        <div className="text-muted" style={{ fontSize: 11 }}>
                            {row.creditsCount} refund{row.creditsCount === 1 ? "" : "s"}
                        </div>
                    </span>
                ) : (
                    <span className="text-muted">-</span>
                ),
            sortable: false,
            center: false,
        },
        {
            name: "Credit",
            selector: (row) => row.creditStatus,
            cell: (row) =>
                row.creditStatusKey === "none" ? (
                    <span className="text-muted">-</span>
                ) : (
                    <span className={`badge badge-${creditBadge(row.creditStatusKey)}`}>{row.creditStatus}</span>
                ),
            sortable: false,
            center: false,
        },
        {
            name: "Reminders",
            selector: (row) => row.remindersSent,
            cell: (row) =>
                row.remindersSent ? (
                    <span title={`Last sent ${row.lastReminderAt}`}>
                        {row.remindersSent}
                        <br />
                        <small className="text-muted">{row.lastReminderAt}</small>
                    </span>
                ) : (
                    <span className="text-muted">-</span>
                ),
            sortable: false,
            center: true,
        },
        {
            name: "Last error",
            selector: (row) => row.lastError,
            cell: (row) => <ErrorPopover row={row} />,
            sortable: false,
            center: false,
        },
    ];

    return (
        <Fragment>
            <Breadcrumb title="Frasers Plus Payments" data={dataMenu} />
            <Container fluid={true} className="product-wrapper">
                <div className="product-grid">
                    <div className="feature-products">
                        <Row>
                            {tiles.map((tile) => (
                                <Col md key={tile.key}>
                                    <Card
                                        className={`mb-3 ${quickFilter === tile.key ? "b-l-primary border-3" : ""}`}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => {
                                            setPage(1);
                                            setQuickFilter(quickFilter === tile.key ? null : tile.key);
                                        }}
                                    >
                                        <CardBody className="p-3">
                                            <h6 className="mb-1 text-muted">{tile.label}</h6>
                                            <h4 className={`mb-0 font-${tile.colour}`}>{tile.value}</h4>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        <Row>
                            <Col sm="3">
                                {/* Filters on the date the Frasers payment was started - every
                                    row has one, whatever became of it. Captured date would hide
                                    exactly the unpaid orders someone comes here to find. */}
                                <Label className="font-weight-bold">Payment date</Label>
                                <div className="dashboard-datepicker">
                                    <DatePicker
                                        dateRange={dateRange}
                                        dateRangeChange={(date) => {
                                            setPage(1);
                                            setDateRange(date);
                                        }}
                                        defaultValue={[TodayDate, thirtyDays]}
                                        disabledDate={afterToday()}
                                        paddingLeft={10}
                                    />
                                </div>
                            </Col>
                            <Col md={3}>
                                <Label className="font-weight-bold">Payment status</Label>
                                <FormGroup>
                                    <Select
                                        placeholder={<div>Payment status</div>}
                                        className="select2-filter"
                                        isClearable
                                        cacheOptions
                                        styles={customStyles}
                                        options={PAYMENT_STATUS_OPTIONS}
                                        onChange={(e) => {
                                            setPage(1);
                                            setPaymentStatus(e ? e.value : null);
                                        }}
                                        components={{
                                            IndicatorSeparator: () => null,
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <Label className="font-weight-bold">Credit status</Label>
                                <FormGroup>
                                    <Select
                                        placeholder={<div>Credit status</div>}
                                        className="select2-filter"
                                        isClearable
                                        cacheOptions
                                        styles={customStyles}
                                        options={CREDIT_STATUS_OPTIONS}
                                        onChange={(e) => {
                                            setPage(1);
                                            setCreditStatus(e ? e.value : null);
                                        }}
                                        components={{
                                            IndicatorSeparator: () => null,
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col sm="3">
                                <Label className="font-weight-bold">Search</Label>
                                <Form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                    }}
                                >
                                    <FormGroup className="m-0">
                                        <Input
                                            name="search"
                                            type="text"
                                            className="form-control"
                                            onChange={(e) => {
                                                clearTimeout(timer);
                                                const value = e.target.value;
                                                const newTimer = setTimeout(() => {
                                                    setPage(1);
                                                    setSearch(value);
                                                }, 500);
                                                setTimer(newTimer);
                                            }}
                                            placeholder="Order number, phone or Frasers reference"
                                        />
                                        <i className="fa fa-search"></i>
                                    </FormGroup>
                                </Form>
                            </Col>
                        </Row>

                        <Row>
                            <Col sm="12" className="mt-3">
                                <Card>
                                    <CardBody>
                                        <div className="table-responsive product-table">
                                            <CommonDataTable
                                                headerColumns={tableColumns}
                                                gridData={frasersState.list}
                                                onView={openPayment}
                                                paginationServer
                                                paginationPerPage={10}
                                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                                onChangePage={(p) => setPage(p)}
                                                onChangeRowsPerPage={(size) => {
                                                    setPage(1);
                                                    setPageSize(size);
                                                }}
                                                paginationTotalRows={frasersState.tableRows}
                                                progressPending={tableLoading}
                                            />
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </div>
                </div>
            </Container>
        </Fragment>
    );
}

export default withReducer([{ order: orderReducer }])(FrasersPlusPayments);
